import { Link } from "react-router-dom";
import { ShieldCheck, Users, Heart, ArrowRight } from "lucide-react";

const HERO_IMG = "/hero-sunflowers-lavender.jpg";

const NATURE_IMG = "/home-roses.jpg";

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                className="relative min-h-[88vh] flex items-center overflow-hidden"
                data-testid="home-hero"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(100deg, rgba(250,249,246,0.96) 0%, rgba(250,249,246,0.82) 40%, rgba(250,249,246,0.35) 100%)",
                        }}
                    />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
                    <div className="max-w-2xl">
                        <p
                            className="pd-fade-up text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium"
                            data-testid="hero-eyebrow"
                        >
                            Present Day Counseling and Recovery
                        </p>
                        <h1
                            className="pd-fade-up pd-delay-100 mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2C332A] break-words"
                            data-testid="hero-headline"
                        >
                            Healing happens{" "}
                            <span className="italic text-[#8C9C88]">
                                in the present.
                            </span>
                        </h1>
                        <p
                            className="pd-fade-up pd-delay-200 mt-8 text-lg text-[#5B6358] leading-relaxed max-w-xl"
                            data-testid="hero-subhead"
                        >
                            A calm, judgment-free space with Ray — a therapist
                            who walks alongside you as you break old cycles,
                            understand yourself more fully, and find your way
                            forward.
                        </p>

                        <div
                            className="pd-fade-up pd-delay-300 mt-10 flex flex-wrap gap-3"
                            data-testid="hero-cta-group"
                        >
                            <Link
                                to="/consultation"
                                className="inline-flex items-center gap-2 rounded-full bg-[#8C9C88] text-white px-7 py-3.5 text-base font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                                data-testid="hero-cta-book"
                            >
                                Book a Consultation
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/about"
                                className="inline-flex items-center gap-2 rounded-full border-2 border-[#8C9C88] text-[#8C9C88] px-7 py-3.5 text-base font-medium transition-all duration-300 hover:bg-[#8C9C88] hover:text-white"
                                data-testid="hero-cta-about"
                            >
                                Learn About Ray
                            </Link>
                            <Link
                                to="/services"
                                className="inline-flex items-center gap-2 rounded-full bg-[#D4C5B3] text-[#2C332A] px-7 py-3.5 text-base font-medium transition-all duration-300 hover:bg-[#C2B29F]"
                                data-testid="hero-cta-services"
                            >
                                See Services
                            </Link>
                        </div>

                        <div
                            className="pd-fade-up pd-delay-400 mt-14 flex flex-wrap gap-6 text-sm text-[#5B6358]"
                            data-testid="hero-trust-row"
                        >
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-[#8C9C88]" />
                                100% Confidential
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Users className="w-4 h-4 text-[#8C9C88]" />
                                Teens &amp; Adults Welcome
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Heart className="w-4 h-4 text-[#8C9C88]" />
                                Warm, Non-Judgmental Care
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* INTRO SECTION */}
            <section
                className="py-24 md:py-32"
                data-testid="home-intro-section"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-5 gap-16 items-center">
                        <div className="md:col-span-3">
                            <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                                Welcome
                            </p>
                            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C332A] leading-tight">
                                You don't need to be fixed.
                                <br />
                                <span className="italic text-[#8C9C88]">
                                    You deserve to be understood.
                                </span>
                            </h2>
                            <p className="mt-8 text-lg text-[#5B6358] leading-relaxed">
                                Present Day Counseling and Recovery is a small,
                                intentional practice rooted in a simple idea:
                                lasting change comes from understanding, not
                                fixing. Ray works closely with teens navigating
                                the day-in, day-out of growing up — school,
                                friendships, family, social media, communication,
                                identity, and the quiet weight of trying to
                                figure yourself out. She meets teens exactly
                                where they are, without judgment, and makes it
                                easy to talk about the things that feel hardest
                                to say.
                            </p>
                            <p className="mt-5 text-lg text-[#5B6358] leading-relaxed">
                                She also supports adults through anxiety, life
                                transitions, and addiction recovery — but the
                                heart of this work is helping everyone feel
                                genuinely seen, safe, and understood.
                            </p>
                            <div className="mt-10">
                                <Link
                                    to="/about"
                                    className="inline-flex items-center gap-2 text-[#2C332A] font-medium border-b-2 border-[#8C9C88] pb-1 transition-colors hover:text-[#8C9C88]"
                                    data-testid="intro-learn-more"
                                >
                                    Meet Ray
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <div className="relative rounded-2xl overflow-hidden border border-[#E3DCC8] shadow-sm aspect-[4/5]">
                                <img
                                    src={NATURE_IMG}
                                    alt="Deep red roses with sage-green leaves — a calming, nature-inspired accent"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST GRID */}
            <section
                className="py-20 bg-[#F5F2EA] pd-grain"
                data-testid="home-trust-section"
            >
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: ShieldCheck,
                                title: "Confidential",
                                body: "Your story is safe. Everything shared in session stays between us.",
                            },
                            {
                                icon: Users,
                                title: "Teens & Adults",
                                body: "Support for teens finding their footing and adults in transition.",
                            },
                            {
                                icon: Heart,
                                title: "Collaborative",
                                body: "We work together — no one gets 'fixed,' but you will be understood.",
                            },
                        ].map((item, i) => (
                            <div
                                key={item.title}
                                className="rounded-2xl p-8 bg-white border border-[#E3DCC8] transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                                data-testid={`trust-card-${i}`}
                            >
                                <item.icon className="w-7 h-7 text-[#8C9C88]" />
                                <h3 className="mt-5 font-heading text-xl text-[#2C332A]">
                                    {item.title}
                                </h3>
                                <p className="mt-3 text-[#5B6358] leading-relaxed">
                                    {item.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24" data-testid="home-final-cta">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C332A]">
                        Ready when you are.
                    </h2>
                    <p className="mt-6 text-lg text-[#5B6358] leading-relaxed max-w-2xl mx-auto">
                        Start with a free 15-minute consultation, or book a
                        full 50-minute session. There's no pressure — just a
                        first step.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/consultation"
                            className="inline-flex items-center gap-2 rounded-full bg-[#8C9C88] text-white px-8 py-3.5 font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                            data-testid="final-cta-book"
                        >
                            Book a Consultation
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="tel:4052540805"
                            className="inline-flex items-center gap-2 rounded-full border-2 border-[#8C9C88] text-[#8C9C88] px-8 py-3.5 font-medium transition-all duration-300 hover:bg-[#8C9C88] hover:text-white"
                            data-testid="final-cta-call"
                        >
                            Call 405-254-0805
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
